/**
 * Free Shipping Progress Bar
 *
 * Keeps the free-shipping progress bar's fill and message in sync with the
 * cart after add-to-cart / quantity-change events. The theme's cart update
 * flow only re-renders sections that live inside a <cart-items-component>
 * (the cart drawer and the /cart page), so a bar rendered elsewhere — like
 * the product page buy-buttons block — needs this small listener to stay
 * accurate without a full page reload. Falls back to fetching /cart.js when
 * the event payload doesn't include cart totals, and fails silently (keeping
 * the last known, server-rendered state) if that fetch doesn't succeed.
 */

const FREE_SHIPPING_THRESHOLD_FALLBACK = 12000000;

class FreeShippingBarComponent extends HTMLElement {
  connectedCallback() {
    this._onCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('cart:update', this._onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this._onCartUpdate);
  }

  /** @param {CustomEvent} event */
  handleCartUpdate(event) {
    const cart = event && event.detail && event.detail.resource;

    if (cart && typeof cart.items_subtotal_price === 'number') {
      this.render(cart.items_subtotal_price, cart.item_count);
      return;
    }

    this.fetchCart();
  }

  fetchCart() {
    let cartUrl = '/cart.js';

    try {
      if (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) {
        cartUrl = window.Theme.routes.cart_url.replace(/\/$/, '') + '.js';
      }
    } catch (error) {
      cartUrl = '/cart.js';
    }

    fetch(cartUrl)
      .then((response) => response.json())
      .then((cart) => {
        if (cart && typeof cart.items_subtotal_price === 'number') {
          this.render(cart.items_subtotal_price, cart.item_count);
        }
      })
      .catch(() => {
        /* Network hiccup: leave the bar showing its last known state. */
      });
  }

  /**
   * @param {number} totalPriceCents
   * @param {number} [itemCount]
   */
  render(totalPriceCents, itemCount) {
    const threshold = Number(this.dataset.threshold) || FREE_SHIPPING_THRESHOLD_FALLBACK;
    const fill = this.querySelector('[data-free-shipping-fill]');
    const message = this.querySelector('[data-free-shipping-message]');

    if (!fill || !message) return;

    const remaining = Math.max(threshold - totalPriceCents, 0);
    const percent = Math.min(Math.max((totalPriceCents / threshold) * 100, 0), 100);

    fill.style.width = percent + '%';

    if (typeof itemCount === 'number' && itemCount === 0) {
      message.textContent = 'Compra desde ' + this.formatMoney(threshold) + ' y obtén 10% de descuento';
    } else if (totalPriceCents >= threshold) {
      message.textContent = '¡Desbloqueaste tu 10% de descuento!';
    } else {
      message.textContent = 'Te faltan ' + this.formatMoney(remaining) + ' para tu 10% de descuento';
    }
  }

  /** @param {number} amountCents */
  formatMoney(amountCents) {
    const value = Math.round(amountCents / 100);

    try {
      return '$' + value.toLocaleString('es-CO');
    } catch (error) {
      return '$' + value;
    }
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBarComponent);
}
